import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, apiUrl, fmtFecha } from '../utils/api';
import { Spinner, Empty, StatusBadge, ConfirmModal } from '../components/UI';
import NuevoClienteForm from '../components/NuevoClienteForm';
import EditorPaquetes from '../components/EditorPaquetes';

export default function Presupuestos() {
  const location = useLocation();
  const navigate = useNavigate();
  const preseleccion = location.state || {};
  const idClienteOrigen = preseleccion.idClienteOrigen;

  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [creando, setCreando] = useState(!!preseleccion.crear);

  const cargar = (q) => {
    setLoading(true);
    const query = q !== undefined ? q : busqueda;
    api(`/presupuestos${query ? `?q=${encodeURIComponent(query)}` : ''}`)
      .then(res => { setPresupuestos(res); setLoading(false); });
  };

  useEffect(() => {
    const t = setTimeout(() => cargar(), busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [busqueda]);

  useEffect(() => {
    if (preseleccion.idAbrir) verDetalle(preseleccion.idAbrir);
  }, []);

  const verDetalle = async (id) => {
    const res = await api(`/presupuestos/${id}`);
    setDetalle(res);
  };

  const volver = () => {
    if (idClienteOrigen) {
      navigate('/clientes', { state: { idAbrir: idClienteOrigen } });
    } else {
      setDetalle(null);
      cargar();
    }
  };

  if (creando) return (
    <CrearPresupuesto onCreado={() => { setCreando(false); cargar(); }} onCancelar={() => setCreando(false)}
      clientePreseleccionado={preseleccion.clientePreseleccionado}
      vehiculoPreseleccionado={preseleccion.vehiculoPreseleccionado}
      paquetesIniciales={preseleccion.paquetesIniciales} />
  );

  if (detalle) return (
    <DetallePresupuesto presupuesto={detalle}
      onVolver={volver}
      onActualizar={() => verDetalle(detalle.id)} />
  );

  const abiertos = presupuestos.filter(p => p.status !== 'completado' && p.status !== 'rechazado');
  const cerrados = presupuestos.filter(p => p.status === 'completado' || p.status === 'rechazado');

  const filaPresupuesto = (p) => (
    <tr key={p.id}>
      <td><span className="badge badge-gris">{p.id}</span></td>
      <td>{fmtFecha(p.fecha)}</td>
      <td style={{ fontWeight: 500 }}>{p.nombre_cliente}</td>
      <td>{p.marca} {p.modelo} {p.matricula ? `| ${p.matricula}` : ''}</td>
      <td>${(p.total || 0).toLocaleString()}</td>
      <td><StatusBadge status={p.status} /></td>
      <td><button className="btn btn-outline btn-sm" onClick={() => verDetalle(p.id)}>Ver</button></td>
    </tr>
  );

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Presupuestos</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>+ Nuevo presupuesto</button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input className="form-input" placeholder="Buscar por cliente, matrícula o N° de presupuesto..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
      </div>

      {loading ? <Spinner texto="Cargando..." /> :
        presupuestos.length === 0 ? <Empty icon="📄" texto="Sin presupuestos" /> :
        <>
          {abiertos.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.5rem' }}>Abiertos ({abiertos.length})</p>
              <div className="tabla-wrap">
                <table className="tabla">
                  <thead>
                    <tr><th>ID</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Total</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {abiertos.map(filaPresupuesto)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {cerrados.length > 0 && (
            <div>
              <p style={{ fontWeight: 600, color: 'var(--gris-4)', marginBottom: '.5rem' }}>Cerrados ({cerrados.length})</p>
              <div className="tabla-wrap">
                <table className="tabla">
                  <thead>
                    <tr><th>ID</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Total</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {cerrados.map(filaPresupuesto)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      }
    </div>
  );
}

export function CrearPresupuesto({ onCreado, onCancelar, clientePreseleccionado, vehiculoPreseleccionado, paquetesIniciales }) {
  const [paso, setPaso] = useState(clientePreseleccionado ? 2 : 1);
  const [cliente, setCliente] = useState(clientePreseleccionado || null);
  const [vehiculo, setVehiculo] = useState(vehiculoPreseleccionado || null);
  const [vehiculos, setVehiculos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [modoNuevo, setModoNuevo] = useState(false);
  const [paquetes, setPaquetes] = useState(paquetesIniciales || []);
  const [km, setKm] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Montevideo' }));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cliente && !vehiculos.length) {
      api(`/vehiculos/cliente/${cliente.id}`).then(setVehiculos);
    }
  }, [cliente]);

  useEffect(() => {
    if (busqueda.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const res = await api(`/clientes?q=${encodeURIComponent(busqueda)}`);
      const vistos = new Set();
      setResultados(res.filter(r => { if (vistos.has(r.id)) return false; vistos.add(r.id); return true; }));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const seleccionarCliente = async (c) => {
    setCliente(c);
    setResultados([]); setBusqueda('');
    const veh = await api(`/vehiculos/cliente/${c.id}`);
    setVehiculos(veh);
    if (veh.length === 1) setVehiculo(veh[0]);
  };

  const handleClienteCreado = (clienteCreado, vehiculoCreado) => {
    setCliente(clienteCreado);
    setVehiculo(vehiculoCreado);
    setModoNuevo(false);
  };

  const total = paquetes.reduce((a, p) => a + (parseFloat(p.precio) || 0), 0);

  const guardar = async () => {
    setGuardando(true); setError('');
    try {
      await api('/presupuestos/', {
        method: 'POST',
        body: JSON.stringify({
          id_cliente: cliente.id, id_vehiculo: vehiculo.id,
          fecha,
          kilometraje: parseInt(String(km).replace(/\D/g, ''), 10) || null,
          paquetes: paquetes.map(p => ({
            nombre: p.nombre, precio: parseFloat(p.precio) || 0,
            tareas: p.tareas.filter(t => t.descripcion.trim()),
            repuestos: p.repuestos.filter(r => r.nombre.trim()).map(r => ({
              nombre: r.nombre, detalle: r.detalle || '',
              costo: parseFloat(r.costo) || 0,
            })),
            costo_repuestos: parseFloat(p.costo_repuestos) || 0,
            costo_mano_obra: parseFloat(p.costo_mano_obra) || 0,
            comision_ml_pct: parseFloat(p.comision_ml_pct) || 15,
            iva_pct: parseFloat(p.iva_pct) || 22,
          })),
        })
      });
      onCreado();
    } catch (e) {
      setError(e.message);
    }
    setGuardando(false);
  };

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Nuevo presupuesto</h1>
        <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
      </div>

      {paso === 1 && (
        <div>
          {modoNuevo && (
            <NuevoClienteForm onCreado={handleClienteCreado} onCancelar={() => setModoNuevo(false)} />
          )}
          {!modoNuevo && !cliente && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="sec-header" style={{ marginBottom: '.5rem' }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Buscar cliente</label>
                <button className="btn btn-outline btn-sm" onClick={() => setModoNuevo(true)}>+ Nuevo cliente</button>
              </div>
              <div className="search-wrap">
                <span className="search-icon">🔍</span>
                <input className="form-input" placeholder="Buscar cliente..." value={busqueda}
                  onChange={e => setBusqueda(e.target.value)} />
              </div>
              {resultados.map(c => (
                <button key={c.id} className="btn btn-outline btn-sm" style={{ justifyContent: 'flex-start', marginTop: '.4rem', width: '100%' }}
                  onClick={() => seleccionarCliente(c)}>
                  {c.nombre} {c.matricula ? `— ${c.matricula}` : ''}
                </button>
              ))}
            </div>
          )}
          {!modoNuevo && cliente && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <p style={{ fontWeight: 600 }}>{cliente.nombre}</p>
              {vehiculos.length > 1 && !vehiculo && (
                <div style={{ marginTop: '.5rem' }}>
                  {vehiculos.map(v => (
                    <button key={v.id} className="btn btn-outline btn-sm" style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '.3rem' }}
                      onClick={() => setVehiculo(v)}>{v.marca} {v.modelo} {v.matricula}</button>
                  ))}
                </div>
              )}
              {vehiculo && <p style={{ color: 'var(--gris-4)', fontSize: '.875rem' }}>🚗 {vehiculo.marca} {vehiculo.modelo}</p>}
            </div>
          )}
          {!modoNuevo && (
            <button className="btn btn-primary btn-full" disabled={!cliente || !vehiculo} onClick={() => setPaso(2)}>
              Continuar →
            </button>
          )}
        </div>
      )}

      {paso === 2 && (
        <div>
          {vehiculo && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="resumen-grid">
                <div className="resumen-card"><div className="label">Cliente</div><div className="valor">{cliente?.nombre}</div></div>
                <div className="resumen-card"><div className="label">Marca</div><div className="valor">{vehiculo.marca || '—'}</div></div>
                <div className="resumen-card"><div className="label">Modelo</div><div className="valor">{vehiculo.modelo || '—'}</div></div>
                <div className="resumen-card"><div className="label">Año</div><div className="valor">{vehiculo.anio || '—'}</div></div>
                <div className="resumen-card"><div className="label">Matrícula</div><div className="valor">{vehiculo.matricula || '—'}</div></div>
                <div className="resumen-card"><div className="label">Motor</div><div className="valor">{vehiculo.motor || '—'}</div></div>
                <div className="resumen-card"><div className="label">Combustible</div><div className="valor">{vehiculo.combustible || '—'}</div></div>
                <div className="resumen-card"><div className="label">Transmisión</div><div className="valor">{vehiculo.transmision || '—'}</div></div>
                <div className="resumen-card"><div className="label">Color</div><div className="valor">{vehiculo.color || '—'}</div></div>
                <div className="resumen-card"><div className="label">Admisión</div><div className="valor">{vehiculo.admision || '—'}</div></div>
                <div className="resumen-card"><div className="label">N° Chasis</div><div className="valor">{vehiculo.n_chasis || '—'}</div></div>
                <div className="resumen-card"><div className="label">N° Motor</div><div className="valor">{vehiculo.n_motor || '—'}</div></div>
              </div>
              <div style={{ marginTop: '.75rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <label style={{ fontSize: '.875rem', fontWeight: 500 }}>Fecha:</label>
                <input type="date" className="form-input" value={fecha} onChange={e => setFecha(e.target.value)} style={{ maxWidth: 200 }} />
                <label style={{ fontSize: '.875rem', fontWeight: 500, marginLeft: '.75rem' }}>Km:</label>
                <input className="form-input" inputMode="numeric" value={km} onChange={e => setKm(e.target.value)}
                  placeholder={vehiculo.ultimo_km ? `Último: ${vehiculo.ultimo_km}` : 'Kilometraje'} style={{ maxWidth: 170 }} />
              </div>
            </div>
          )}

          <EditorPaquetes paquetes={paquetes} setPaquetes={setPaquetes} vehiculo={vehiculo} mostrarCalculadora={true} />

          {total > 0 && (
            <div style={{ background: 'var(--azul)', color: 'white', borderRadius: 'var(--radio)', padding: '.75rem 1.25rem',
                         display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: '1rem' }}>
              <span>TOTAL PRESUPUESTADO</span><span>${total.toLocaleString()}</span>
            </div>
          )}

          {error && <p style={{ color: 'var(--rojo)', fontSize: '.875rem', marginBottom: '.75rem' }}>{error}</p>}
          {guardando && <Spinner texto="Guardando..." />}

          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button className="btn btn-secondary" onClick={() => setPaso(1)}>← Atrás</button>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={paquetes.length === 0 || guardando} onClick={guardar}>
              ✅ Aceptar y crear presupuesto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Datos completos del cliente y del vehículo (pantalla de detalle y de edición del presupuesto)
function DatosPresupuesto({ presupuesto, mostrarEstado, kmEditable, setKm }) {
  const dato = (etiqueta, valor) => (
    <div className="resumen-card"><div className="label">{etiqueta}</div><div className="valor">{valor || '—'}</div></div>
  );
  return (
    <>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cliente</p>
        <div className="resumen-grid">
          {dato('Nombre', presupuesto.nombre_cliente)}
          {dato('Celular', presupuesto.celular)}
          {dato('RUT o CI', presupuesto.rut)}
          {dato('Dirección', presupuesto.direccion)}
          {mostrarEstado && (
            <div className="resumen-card"><div className="label">Estado</div><div className="valor"><StatusBadge status={presupuesto.status} /></div></div>
          )}
        </div>
      </div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Vehículo</p>
        <div className="resumen-grid">
          {dato('Marca', presupuesto.marca)}
          {dato('Modelo', presupuesto.modelo)}
          {dato('Año', presupuesto.anio)}
          {dato('Color', presupuesto.color)}
          {dato('Motor', presupuesto.motor)}
          {dato('Combustible', presupuesto.combustible)}
          {dato('Transmisión', presupuesto.transmision)}
          {dato('Admisión', presupuesto.admision)}
          {dato('Matrícula', presupuesto.matricula)}
          {dato('N° de chasis', presupuesto.n_chasis)}
          {dato('N° de motor', presupuesto.n_motor)}
          {!setKm && dato('Kilometraje', presupuesto.kilometraje != null ? `${Number(presupuesto.kilometraje).toLocaleString('es-UY')} km` : '')}
        </div>
        {setKm && (
          <div style={{ marginTop: '.75rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <label style={{ fontSize: '.875rem', fontWeight: 500 }}>Kilometraje:</label>
            <input className="form-input" inputMode="numeric" placeholder="Kilometraje" value={kmEditable}
              onChange={e => setKm(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
        )}
      </div>
    </>
  );
}

function DetallePresupuesto({ presupuesto, onVolver, onActualizar }) {
  const [seleccionados, setSeleccionados] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [ordenGenerada, setOrdenGenerada] = useState(null);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState(false);
  const [editando, setEditando] = useState(false);

  if (editando) return (
    <EditarPresupuesto
      presupuesto={presupuesto}
      onGuardado={() => { setEditando(false); onActualizar(); }}
      onCancelar={() => setEditando(false)}
    />
  );

  const enviarPorEmail = async () => {
    setEnviandoEmail(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const res = await fetch(apiUrl(`/descargas/presupuestos/${presupuesto.id}/pdf/enviar-email`), {
        method: 'POST', signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('No se pudo enviar');
      setEmailEnviado(true);
    } catch {
      alert('No se pudo enviar el email. Puede que el servidor de correo esté tardando demasiado — probá de nuevo en un rato.');
    }
    setEnviandoEmail(false);
  };

  const toggleSeleccion = (id) => {
    setSeleccionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const generarOrden = async () => {
    setGenerando(true);
    try {
      const res = await api(`/presupuestos/${presupuesto.id}/generar-orden`, {
        method: 'POST',
        body: JSON.stringify({ ids_paquetes: seleccionados })
      });
      setOrdenGenerada(res.id_orden);
      setSeleccionados([]);
    } catch { }
    setGenerando(false);
  };

  const descartarPaquete = async (idPaquete) => {
    await api(`/clientes/presupuesto-paquete/${idPaquete}/descartar`, { method: 'PATCH' });
    onActualizar();
  };

  const eliminarPresupuesto = async () => {
    await api(`/presupuestos/${presupuesto.id}`, { method: 'DELETE' });
    onVolver();
  };

  const pendientes = presupuesto.paquetes.filter(p => p.status === 'pendiente');
  const ejecutados = presupuesto.paquetes.filter(p => p.status === 'ejecutado');
  const descartados = presupuesto.paquetes.filter(p => p.status === 'descartado');

  return (
    <div className="page-content">
      <div className="sec-header">
        <h2 className="page-title" style={{ marginBottom: 0 }}>{presupuesto.id}</h2>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={onVolver}>← Volver</button>
          <button className="btn btn-outline btn-sm" onClick={() => setEditando(true)}>✏️ Editar</button>
          <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }}
            onClick={() => setConfirmandoEliminar(true)}>🗑️ Eliminar</button>
        </div>
      </div>

      {confirmandoEliminar && (
        <ConfirmModal
          mensaje="¿Eliminar este presupuesto? Quedará oculto de las listas, pero se puede recuperar si hace falta."
          onConfirm={eliminarPresupuesto}
          onCancel={() => setConfirmandoEliminar(false)}
        />
      )}

      <DatosPresupuesto presupuesto={presupuesto} mostrarEstado />

      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <a href={apiUrl(`/descargas/presupuestos/${presupuesto.id}/pdf`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
          📄 Ver PDF del presupuesto
        </a>
        <button className="btn btn-outline btn-sm" disabled={enviandoEmail || emailEnviado} onClick={enviarPorEmail}>
          {emailEnviado ? '✅ Enviado' : enviandoEmail ? 'Enviando...' : '📧 Enviar por email'}
        </button>
      </div>

      {ordenGenerada && (
        <div className="card" style={{ marginBottom: '1rem', background: 'var(--verde-claro)', borderLeft: '3px solid var(--verde)' }}>
          <p style={{ color: 'var(--verde)', fontWeight: 600 }}>✅ Orden {ordenGenerada} creada en estado borrador.</p>
          <p style={{ fontSize: '.875rem', color: 'var(--gris-5)' }}>Ya podés ir a "Órdenes" para imprimirla y seguir el flujo normal.</p>
        </div>
      )}

      {pendientes.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.6rem' }}>
            Paquetes pendientes — seleccioná los que el cliente acepta
          </p>
          {pendientes.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-start',
                                     padding: '.5rem 0', borderBottom: '1px solid var(--gris-2)' }}>
              <input type="checkbox" checked={seleccionados.includes(p.id)} onChange={() => toggleSeleccion(p.id)}
                style={{ marginTop: '.2rem' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500 }}>{p.nombre}</span>
                  <span style={{ fontWeight: 600 }}>${p.precio.toLocaleString()}</span>
                </div>
                {p.tareas.map((t, j) => <p key={j} style={{ fontSize: '.8125rem', color: 'var(--gris-5)' }}>• {t.descripcion}</p>)}
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => descartarPaquete(p.id)} title="Marcar como descartado (no se va a hacer)">
                Descartar
              </button>
            </div>
          ))}
          <button className="btn btn-primary btn-full" style={{ marginTop: '.75rem' }}
            disabled={seleccionados.length === 0 || generando} onClick={generarOrden}>
            {generando ? 'Generando...' : `✅ Aceptar y generar orden con ${seleccionados.length} paquete(s) seleccionado(s)`}
          </button>
        </div>
      )}

      {ejecutados.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--gris-4)', marginBottom: '.6rem' }}>Ya ejecutados</p>
          {ejecutados.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '.4rem 0',
                                     borderBottom: '1px solid var(--gris-2)', fontSize: '.875rem', color: 'var(--gris-4)' }}>
              <span>{p.nombre}</span>
              <span className="badge badge-verde">{p.id_orden_generada}</span>
            </div>
          ))}
        </div>
      )}

      {descartados.length > 0 && (
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--gris-4)', marginBottom: '.6rem' }}>Descartados</p>
          {descartados.map(p => (
            <div key={p.id} style={{ padding: '.4rem 0', borderBottom: '1px solid var(--gris-2)', fontSize: '.875rem', color: 'var(--gris-4)' }}>
              {p.nombre}
            </div>
          ))}
        </div>
      )}

      {pendientes.length === 0 && ejecutados.length === 0 && descartados.length === 0 && <Empty icon="📄" texto="Sin paquetes" />}
    </div>
  );
}

function EditarPresupuesto({ presupuesto, onGuardado, onCancelar }) {
  const [paquetes, setPaquetes] = useState(() =>
    presupuesto.paquetes.map(p => ({
      nombre: p.nombre, precio: p.precio,
      tareas: p.tareas.map(t => ({ descripcion: t.descripcion })),
      repuestos: p.repuestos.map(r => ({ nombre: r.nombre, detalle: r.detalle || '', codigo: r.codigo || '', costo_taller: r.costo_taller || '', costo: '' })),
      costo_repuestos: 0, costo_mano_obra: 0, comision_ml_pct: 15, iva_pct: 22,
    }))
  );
  const [km, setKm] = useState(presupuesto.kilometraje != null ? String(presupuesto.kilometraje) : '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    setGuardando(true); setError('');
    try {
      await api(`/presupuestos/${presupuesto.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          id_cliente: presupuesto.id_cliente,
          id_vehiculo: presupuesto.id_vehiculo,
          kilometraje: parseInt(String(km).replace(/\D/g, ''), 10) || null,
          paquetes: paquetes.map(p => ({
            nombre: p.nombre, precio: parseFloat(p.precio) || 0,
            tareas: p.tareas.filter(t => t.descripcion.trim()),
            repuestos: p.repuestos.filter(r => r.nombre.trim()).map(r => ({
              nombre: r.nombre, detalle: r.detalle || '', costo: parseFloat(r.costo) || 0,
            })),
            costo_repuestos: parseFloat(p.costo_repuestos) || 0,
            costo_mano_obra: parseFloat(p.costo_mano_obra) || 0,
            comision_ml_pct: parseFloat(p.comision_ml_pct) || 15,
            iva_pct: parseFloat(p.iva_pct) || 22,
          })),
        })
      });
      onGuardado();
    } catch (e) { setError(e.message); }
    setGuardando(false);
  };

  const total = paquetes.reduce((a, p) => a + (parseFloat(p.precio) || 0), 0);

  return (
    <div className="page-content">
      <div className="sec-header">
        <h2 className="page-title" style={{ marginBottom: 0 }}>Editar {presupuesto.id}</h2>
        <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
      </div>
      <DatosPresupuesto presupuesto={presupuesto} kmEditable={km} setKm={setKm} />
      <EditorPaquetes paquetes={paquetes} setPaquetes={setPaquetes}
        vehiculo={{ marca: presupuesto.marca, modelo: presupuesto.modelo }} mostrarCalculadora={true} />
      {total > 0 && (
        <div style={{ background: 'var(--azul)', color: 'white', borderRadius: 'var(--radio)', padding: '.75rem 1.25rem',
                     display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: '1rem' }}>
          <span>TOTAL PRESUPUESTADO</span><span>${total.toLocaleString()}</span>
        </div>
      )}
      {error && <p style={{ color: 'var(--rojo)', fontSize: '.875rem', marginBottom: '.75rem' }}>{error}</p>}
      {guardando && <Spinner texto="Guardando..." />}
      <button className="btn btn-primary btn-full" disabled={guardando} onClick={guardar}>
        ✅ Aceptar y guardar cambios
      </button>
    </div>
  );
}
